import React from 'react';
import { Grid, Anchor, Stack, Image, Box, Group, Divider } from '@mantine/core';
import { resolveButtonLink } from '../../../../shared/boxes/button/buttonLink';
import VariableImage from '../../../media/VariableImage';
import PuckInlineRichText from '../../proposal/ProposalRichText/PuckInlineRichText';
import { linkStyleToCss } from './SiteFooterLinkStyleField';
import { columnOfIndex, distributeIntoColumns } from './footerLinkColumns';
import ConsentFooterLinks from '../../../consent/ConsentFooterLinks';
import classes from './SiteFooter.module.css';

/** Couleur de texte par défaut (fond sombre par défaut) : plus un paramètre
 *  exposé — chaque texte riche (nom, copyright) ou les liens (linkStyle) se
 *  recolorent individuellement via leur propre toolbar au besoin. */
const BASE_TEXT_COLOR = '#ffffff';

/** Marge horizontale par défaut (16px). */
const DEFAULT_MARGIN_X = '1rem';

/**
 * Footer — 3 variantes :
 *   columns   : colonnes de liens (multi-colonnes, défaut)
 *   centered  : contenu centré, liens en ligne
 *   minimal   : juste copyright + quelques liens
 */
const SiteFooter = ({
  id,
  puck,
  companyName = 'Nom de la société',
  logoUrl = '',
  logoUrlVariable = null,
  links = [],
  linkStyle = null,
  columns = 2,
  copyrightText = '© 2026 Nom de la société — Mention légale',
  backgroundColor = '#1a1a1a',
  paddingX,
  variant = 'columns',
}) => {
  const isEditing = !!puck?.isEditing;
  // Couleur forcée en inline (jamais via classe/var CSS) : Mantine applique
  // sa propre couleur de lien (bleu) sur Anchor avec une spécificité CSS
  // équivalente à une classe — l'ordre de chargement des styles peut la
  // faire gagner. Seul l'inline gagne toujours.
  const linkCss = { ...linkStyleToCss(linkStyle), color: linkStyle?.color || BASE_TEXT_COLOR };
  // Marge horizontale (pas un padding : à 0 le contenu touche les bords, sans
  // résidu de largeur max) — même mécanique que le menu, sans le palier
  // hamburger (propre au menu, sans équivalent ici).
  const marginX = paddingX || DEFAULT_MARGIN_X;
  const constrain = (children, py) => (
    <Box mx={marginX} py={py} style={{ boxSizing: 'border-box' }}>{children}</Box>
  );

  // Logo image + nom de la société peuvent coexister (comme le menu) : image
  // à gauche, nom juste à côté. Le nom est un texte riche indépendant, y
  // compris du texte de copyright ci-dessous (décorrélés : éditer l'un ne
  // touche pas l'autre).
  const altText = String(companyName || '').replace(/<[^>]+>/g, '').trim() || 'Logo';
  const logoImage = (
    <VariableImage
      blockId={id}
      urlProp="logoUrl"
      src={logoUrl}
      binding={logoUrlVariable}
      isEditing={isEditing}
      renderImage={(src) => src && <Image src={src} alt={altText} h={36} w="auto" fit="contain" />}
    />
  );
  const nameElem = (
    <PuckInlineRichText
      id={id}
      puck={puck}
      html={companyName}
      contentProp="companyName"
      style={{ fontWeight: 700, fontSize: '1.125rem', color: BASE_TEXT_COLOR, ...(isEditing ? { minWidth: 120 } : {}) }}
      minHeight={isEditing ? 24 : undefined}
      placeholder="Nom de la société"
      reducedToolbar
    />
  );
  const logoElem = (
    <Group gap="sm" wrap="nowrap" align="center">
      {logoImage}
      {nameElem}
    </Group>
  );

  const linkElems = links.map((link, i) => (
    <Anchor
      key={i}
      // Un lien sans cible reste un repère visible dans l'éditeur, d'où le '#'.
      {...withFallbackHref(resolveButtonLink(link.target, link.url))}
      size="sm"
      style={linkCss}
    >
      {link.label}
    </Anchor>
  ));

  // Jamais dans `links` : les stocker dans les données les rendrait
  // supprimables, renommables et redirigeables.
  const consentProps = { linkStyle: linkCss, backgroundColor, isEditing };
  const consentLinks = <ConsentFooterLinks {...consentProps} />;

  // Ligne de copyright : texte riche entièrement libre, éditable au même
  // titre que le nom ci-dessus, mais sans lien automatique avec lui (l'année,
  // le nom, la mention légale sont tous modifiables directement dans le texte).
  const copyrightElem = (
    <PuckInlineRichText
      id={id}
      puck={puck}
      html={copyrightText}
      contentProp="copyrightText"
      style={{ fontSize: '0.75rem', color: BASE_TEXT_COLOR }}
      placeholder="© 2026 Nom de la société — Mention légale"
      reducedToolbar
    />
  );

  /* ── Variante : centered ───────────────────────────── */
  if (variant === 'centered') {
    return (
      <footer className={classes.footer} style={{ backgroundColor }}>
        {constrain(
          <Stack align="center" gap="md">
            {logoElem}
            <Group gap="lg" justify="center">{linkElems}{consentLinks}</Group>
            <Divider style={{ borderColor: `${BASE_TEXT_COLOR}22`, width: '100%' }} />
            {copyrightElem}
          </Stack>,
          'xl',
        )}
      </footer>
    );
  }

  /* ── Variante : minimal ────────────────────────────── */
  if (variant === 'minimal') {
    return (
      <footer className={classes.footer} style={{ backgroundColor }}>
        {constrain(
          <Group justify="space-between" wrap="wrap" gap="sm">
            {logoElem}
            <Group gap="md">{linkElems}{consentLinks}</Group>
            {copyrightElem}
          </Group>,
          'md',
        )}
      </footer>
    );
  }

  /* ── Variante par défaut : columns ─────────────────── */
  const linkColumns = distributeIntoColumns(links, columns);
  const colSpan = Math.floor(12 / Math.max(1, linkColumns.length));
  // Les liens de consentement se posent ensemble, là où l'élément suivant de la
  // liste serait tombé — le flux continue, sans qu'ils entrent dans `links`.
  // Répartis un par colonne, ils se retrouvaient à des hauteurs différentes et
  // se lisaient comme trois liens sans rapport.
  const consentColumn = columnOfIndex(links.length, linkColumns.length);

  return (
    <footer className={classes.footer} style={{ backgroundColor }}>
      {constrain(
        // Logo/nom sur sa propre ligne, liens sur une grille SÉPARÉE en
        // dessous : les mélanger dans une seule Grid (logo + colonnes de
        // liens côte à côte) fait passer certaines colonnes à la ligne du
        // logo et d'autres non dès que le total de spans dépasse 12 — les
        // liens doivent TOUJOURS être sous le nom, jamais à côté.
        <Stack gap="xl">
          {logoElem}
          {linkColumns.length > 0 ? (
            <Grid gap="xl">
              {linkColumns.map((col, idx) => (
                <Grid.Col key={idx} span={{ base: 6, sm: colSpan }}>
                  <Stack gap={6}>
                    {col.map((link, i) => (
                      <Anchor
                        key={i}
                        {...withFallbackHref(resolveButtonLink(link.target, link.url))}
                        size="sm"
                        style={linkCss}
                      >
                        {link.label}
                      </Anchor>
                    ))}
                    {/* Ils prolongent le flux, groupés, mais restent hors des
                        données : non supprimables. */}
                    {idx === consentColumn && consentLinks}
                  </Stack>
                </Grid.Col>
              ))}
            </Grid>
          ) : (
            // Un pied de page sans aucun lien reste tenu de porter ceux-ci.
            <Stack gap={6} align="flex-start">{consentLinks}</Stack>
          )}
          <Box className={classes.bottom} style={{ borderColor: `${BASE_TEXT_COLOR}22` }}>
            {copyrightElem}
          </Box>
        </Stack>,
        'xl',
      )}
    </footer>
  );
};

export default SiteFooter;

/** Un lien de pied de page sans cible reste visible : il garde un href inerte. */
function withFallbackHref(anchorProps) {
  return anchorProps.href ? anchorProps : { ...anchorProps, href: '#' };
}
